import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import LoginPromptModal from '../components/LoginPromptModal';

const PRIMARY = '#2DBCAF';

interface Measurement {
  _id: string;
  date: string;
  height: number;
  weight: number;
  headCirc?: number;
  notes?: string;
}

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 100px;
`;

const Header = styled.header`
  background: white;
  padding: 16px 16px 14px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #64748b;
  .material-symbols-outlined { font-size: 24px; }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  flex: 1;
`;

const ChildTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(45,188,175,0.1);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: ${PRIMARY};
`;

const LatestCard = styled.div`
  margin: 16px;
  background: white;
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;

const LatestTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  .material-symbols-outlined { font-size: 18px; }
`;

const LatestGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const StatBox = styled.div<{ $color: string }>`
  background: ${p => `${p.$color}10`};
  border-radius: 14px;
  padding: 16px;
  text-align: center;
  .icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${p => p.$color};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;
    .material-symbols-outlined { font-size: 22px; color: white; }
  }
  .value {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
  }
  .unit {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
  }
  .label {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 4px;
  }
`;

const AddBtn = styled.button`
  width: calc(100% - 32px);
  margin: 0 16px 16px;
  height: 52px;
  background: ${PRIMARY};
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  .material-symbols-outlined { font-size: 20px; }
  &:hover { opacity: 0.9; }
`;

const SectionTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  padding: 0 16px 8px;
  margin: 0;
`;

const HistoryList = styled.div`
  padding: 0 16px;
`;

const HistoryItem = styled.div`
  background: white;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const HistoryDate = styled.div`
  width: 50px;
  text-align: center;
  .day { font-size: 20px; font-weight: 800; color: #0f172a; }
  .month { font-size: 11px; color: #64748b; font-weight: 600; }
`;

const HistoryData = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
  .item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #475569;
    .material-symbols-outlined { font-size: 18px; color: #94a3b8; }
    strong { font-weight: 700; color: #0f172a; }
  }
`;

const HistoryActions = styled.div`
  display: flex;
  gap: 4px;
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: ${p => p.$danger ? '#ef4444' : '#94a3b8'};
  &:hover { background: ${p => p.$danger ? '#fef2f2' : '#f1f5f9'}; }
  .material-symbols-outlined { font-size: 18px; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
  .material-symbols-outlined { font-size: 48px; color: #cbd5e1; }
  h3 { font-size: 16px; font-weight: 700; color: #475569; margin: 12px 0 6px; }
  p { font-size: 13px; margin: 0; }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 24px;
  padding: 28px 24px 24px;
  width: 100%;
  max-width: 400px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 20px;
  text-align: center;
`;

const ModalField = styled.div`
  margin-bottom: 16px;
`;

const ModalLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 8px;
  .optional {
    font-weight: 500;
    color: #94a3b8;
    font-size: 11px;
  }
`;

const ModalInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  background: #f8fafc;
  color: #0f172a;
  box-sizing: border-box;
  -webkit-appearance: none;
  &:focus { outline: none; border-color: ${PRIMARY}; background: white; }
  &::placeholder { color: #94a3b8; }
  &[type="date"] {
    &::-webkit-date-and-time-value { text-align: left; }
  }
`;

const ModalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;

const ModalBtns = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  height: 50px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: ${p => p.$primary ? 'none' : '2px solid #e2e8f0'};
  background: ${p => p.$primary ? PRIMARY : 'white'};
  color: ${p => p.$primary ? 'white' : '#64748b'};
  transition: opacity 0.2s, transform 0.1s;
  &:hover { opacity: 0.9; }
  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

function formatDate(dateStr: string): { day: string; month: string; full: string } {
  const d = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return {
    day: d.getDate().toString(),
    month: months[d.getMonth()],
    full: `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
  };
}

export default function GrowthPage() {
  const navigate = useNavigate();
  const { user, activeChild, refetchUser } = useAuth();
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    height: '',
    weight: '',
    headCirc: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchMeasurements = async () => {
    if (!activeChild) return;
    try {
      const res = await fetch(`/api/children/${activeChild._id}/measurements`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) setMeasurements(data.measurements);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchMeasurements();
  }, [activeChild?._id]);

  const openAddModal = () => {
    setEditingId(null);
    setForm({
      date: new Date().toISOString().split('T')[0],
      height: '',
      weight: '',
      headCirc: '',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (m: Measurement) => {
    setEditingId(m._id);
    setForm({
      date: m.date.split('T')[0],
      height: m.height.toString(),
      weight: m.weight.toString(),
      headCirc: m.headCirc?.toString() || '',
      notes: m.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!activeChild || !form.height || !form.weight) return;
    setSaving(true);

    try {
      const url = editingId 
        ? `/api/children/${activeChild._id}/measurements/${editingId}`
        : `/api/children/${activeChild._id}/measurements`;
      
      await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: form.date,
          height: Number(form.height),
          weight: Number(form.weight),
          headCirc: form.headCirc ? Number(form.headCirc) : undefined,
          notes: form.notes || undefined
        })
      });

      setShowModal(false);
      fetchMeasurements();
      refetchUser();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!activeChild || !confirm('Hapus data pengukuran ini?')) return;
    try {
      await fetch(`/api/children/${activeChild._id}/measurements/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchMeasurements();
    } catch {}
  };

  const latest = measurements[0];

  // Show login modal if not logged in
  if (!user) {
    return (
      <>
        <SEOMeta
          title="Grafik Pertumbuhan - KinderGame.id"
          description="Pantau pertumbuhan tinggi dan berat badan anak"
          path="/tumbuh-kembang/growth"
        />
        <PageWrapper>
          <Header>
            <HeaderTop>
              <BackBtn onClick={() => navigate('/tumbuh-kembang')}>
                <span className="material-symbols-outlined">arrow_back</span>
              </BackBtn>
              <HeaderTitle>Grafik Pertumbuhan</HeaderTitle>
            </HeaderTop>
          </Header>
        </PageWrapper>
        <BottomNavBar />
        <LoginPromptModal
          title="Ups! Kamu Belum Login"
          subtitle="Login untuk melihat grafik pertumbuhan anak"
          onClose={() => navigate('/tumbuh-kembang')}
        />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        title="Grafik Pertumbuhan - KinderGame.id"
        description="Pantau pertumbuhan tinggi dan berat badan anak"
        path="/tumbuh-kembang/growth"
      />
      <PageWrapper>
        <Header>
          <HeaderTop>
            <BackBtn onClick={() => navigate('/tumbuh-kembang')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </BackBtn>
            <HeaderTitle>Grafik Pertumbuhan</HeaderTitle>
            {activeChild && (
              <ChildTag>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>face</span>
                {activeChild.name}
              </ChildTag>
            )}
          </HeaderTop>
        </Header>

        {latest ? (
          <LatestCard>
            <LatestTitle>
              <span className="material-symbols-outlined">event</span>
              Pengukuran Terakhir — {formatDate(latest.date).full}
            </LatestTitle>
            <LatestGrid>
              <StatBox $color="#10B981">
                <div className="icon">
                  <span className="material-symbols-outlined">straighten</span>
                </div>
                <div className="value">{latest.height}</div>
                <div className="unit">cm</div>
                <div className="label">Tinggi Badan</div>
              </StatBox>
              <StatBox $color="#3B82F6">
                <div className="icon">
                  <span className="material-symbols-outlined">monitor_weight</span>
                </div>
                <div className="value">{latest.weight}</div>
                <div className="unit">kg</div>
                <div className="label">Berat Badan</div>
              </StatBox>
            </LatestGrid>
          </LatestCard>
        ) : !loading && (
          <EmptyState>
            <span className="material-symbols-outlined">monitoring</span>
            <h3>Belum Ada Data</h3>
            <p>Tambahkan pengukuran pertama untuk anak Anda</p>
          </EmptyState>
        )}

        <AddBtn onClick={openAddModal}>
          <span className="material-symbols-outlined">add</span>
          Tambah Pengukuran
        </AddBtn>

        {measurements.length > 0 && (
          <>
            <SectionTitle>Riwayat Pengukuran</SectionTitle>
            <HistoryList>
              {measurements.map(m => {
                const d = formatDate(m.date);
                return (
                  <HistoryItem key={m._id}>
                    <HistoryDate>
                      <div className="day">{d.day}</div>
                      <div className="month">{d.month}</div>
                    </HistoryDate>
                    <HistoryData>
                      <div className="item">
                        <span className="material-symbols-outlined">straighten</span>
                        <strong>{m.height}</strong> cm
                      </div>
                      <div className="item">
                        <span className="material-symbols-outlined">monitor_weight</span>
                        <strong>{m.weight}</strong> kg
                      </div>
                    </HistoryData>
                    <HistoryActions>
                      <ActionBtn onClick={() => openEditModal(m)}>
                        <span className="material-symbols-outlined">edit</span>
                      </ActionBtn>
                      <ActionBtn $danger onClick={() => handleDelete(m._id)}>
                        <span className="material-symbols-outlined">delete</span>
                      </ActionBtn>
                    </HistoryActions>
                  </HistoryItem>
                );
              })}
            </HistoryList>
          </>
        )}
      </PageWrapper>

      <BottomNavBar />

      {/* Add/Edit Modal */}
      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>{editingId ? 'Edit Pengukuran' : 'Tambah Pengukuran'}</ModalTitle>
            
            <ModalField>
              <ModalLabel>Tanggal</ModalLabel>
              <ModalInput
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
              />
            </ModalField>

            <ModalRow>
              <ModalField>
                <ModalLabel>Tinggi (cm)</ModalLabel>
                <ModalInput
                  type="number"
                  placeholder="65"
                  value={form.height}
                  onChange={e => setForm({ ...form, height: e.target.value })}
                  step="0.1"
                />
              </ModalField>
              <ModalField>
                <ModalLabel>Berat (kg)</ModalLabel>
                <ModalInput
                  type="number"
                  placeholder="7.5"
                  value={form.weight}
                  onChange={e => setForm({ ...form, weight: e.target.value })}
                  step="0.1"
                />
              </ModalField>
            </ModalRow>

            <ModalField>
              <ModalLabel>L. Kepala (cm) <span className="optional">opsional</span></ModalLabel>
              <ModalInput
                type="number"
                placeholder="42"
                value={form.headCirc}
                onChange={e => setForm({ ...form, headCirc: e.target.value })}
                step="0.1"
              />
            </ModalField>

            <ModalField>
              <ModalLabel>Catatan <span className="optional">opsional</span></ModalLabel>
              <ModalInput
                type="text"
                placeholder="Contoh: Setelah imunisasi, sehat"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </ModalField>

            <ModalBtns>
              <ModalBtn onClick={() => setShowModal(false)}>Batal</ModalBtn>
              <ModalBtn 
                $primary 
                onClick={handleSave}
                disabled={saving || !form.height || !form.weight}
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </ModalBtn>
            </ModalBtns>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
}
